import React from 'react';
import { X, Gift, Sparkles, Mail, PartyPopper } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';

const WelcomeModal = ({ name, onClose }) => {
  const { language } = useLanguage();
  const hu = language === 'hu';

  const tiers = [
    { orders: hu ? '2. rendeléstől' : 'From order #2', percent: '5%', min: '' },
    { orders: hu ? '4. rendeléstől (min. 15 000 Ft)' : 'From order #4 (min. 15,000 Ft)', percent: '10%', min: '' },
    { orders: hu ? '7. rendeléstől (min. 20 000 Ft)' : 'From order #7 (min. 20,000 Ft)', percent: '15%', min: '' },
    { orders: hu ? '10. rendeléstől (min. 25 000 Ft)' : 'From order #10 (min. 25,000 Ft)', percent: '20%', min: '' },
  ];

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4 py-6 animate-in fade-in duration-300"
      data-testid="welcome-modal"
    >
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-gradient-to-b from-amber-50 to-white shadow-2xl animate-in zoom-in-95 duration-300">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 rounded-full p-1.5 text-zinc-500 transition-colors hover:bg-zinc-200 hover:text-zinc-900"
          data-testid="welcome-modal-close"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="rounded-t-2xl bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-500 px-7 py-6 text-center">
          <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-white/90 shadow">
            <PartyPopper className="h-7 w-7 text-orange-500" />
          </div>
          <h2 className="text-2xl font-extrabold text-zinc-900" data-testid="welcome-modal-title">
            {hu ? `Kedves ${name}!` : `Dear ${name}!`}
          </h2>
        </div>

        <div className="px-7 py-6">
          <p className="mb-5 text-[15px] leading-relaxed text-zinc-700 text-center">
            {hu
              ? 'Köszönjük, hogy regisztráltál a ShishaVibes oldalán! Mostantól nyomon követheted a rendeléseidet, és részt veszel a hűségprogramunkban.'
              : 'Thank you for registering at ShishaVibes! You can now track your orders and take part in our loyalty program.'}
          </p>

          {/* Loyalty program */}
          <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-5">
            <div className="mb-3 flex items-center gap-2">
              <Gift className="h-5 w-5 text-orange-500" />
              <h3 className="text-lg font-bold text-zinc-900">{hu ? 'Mérföldkő hűségprogram' : 'Milestone Loyalty Program'}</h3>
            </div>
            <p className="mb-3 text-sm text-zinc-600">
              {hu
                ? 'Minél többet rendelsz, annál nagyobb kedvezményt kapsz! A kedvezmények a rendeléseid száma alapján oldódnak fel:'
                : 'The more you order, the bigger discount you get! Discounts unlock based on your number of orders:'}
            </p>
            <div className="overflow-hidden rounded-lg border border-amber-200 bg-white">
              {tiers.map((tier, i) => (
                <div
                  key={i}
                  className={`flex items-center justify-between px-4 py-2.5 ${i < tiers.length - 1 ? 'border-b border-amber-100' : ''}`}
                >
                  <span className="text-sm text-zinc-700">{tier.orders}</span>
                  <span className="text-base font-extrabold text-orange-600">{tier.percent}</span>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs leading-relaxed text-zinc-500">
              {hu
                ? 'A ciklus minden 10 rendelés után újraindul (12., 14., 17., 20. rendelés stb.), így folyamatosan gyűjtheted a kedvezményeket. A feloldott kedvezmény a következő 2 rendelésig érvényes. A hűségkedvezmény nem vonható össze promóciós kóddal és nem érvényes akciós termékekre.'
                : 'The cycle restarts every 10 orders (#12, #14, #17, #20, etc.), so you can keep collecting discounts. An unlocked discount stays valid for the next 2 orders. It cannot be combined with a promo code and does not apply to sale items.'}
            </p>
          </div>

          {/* Spam note */}
          <div className="mt-5 flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4" data-testid="welcome-spam-note">
            <Mail className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
            <p className="text-sm leading-relaxed text-zinc-700">
              {hu ? (
                <>
                  <span className="font-semibold text-blue-700">Fontos:</span> a visszaigazoló és rendelési e-mailjeink néha a{' '}
                  <span className="font-semibold">SPAM / Levélszemét</span> mappába kerülhetnek. Kérjük, nézd meg ott is, és
                  jelöld meg az e-mailünket mint <span className="font-semibold">„Nem spam"</span>, hogy a jövőben mindig
                  megkapd a rendelésed állapotát és a kedvezményeidet!
                </>
              ) : (
                <>
                  <span className="font-semibold text-blue-700">Important:</span> our confirmation and order emails may
                  sometimes land in your <span className="font-semibold">SPAM / Junk</span> folder. Please check there and
                  mark our email as <span className="font-semibold">"Not spam"</span> so you always receive your order
                  status and discounts!
                </>
              )}
            </p>
          </div>

          <Button
            onClick={onClose}
            className="mt-6 w-full bg-gradient-to-r from-yellow-400 to-orange-500 py-6 font-bold text-black hover:from-yellow-300 hover:to-orange-400"
            data-testid="welcome-modal-cta"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            {hu ? 'Kezdjünk vásárolni!' : "Let's start shopping!"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal;
